import { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import DownloadIcon from "@mui/icons-material/Download";
import { useSelector } from "react-redux";
import { findByCurrentUser, save, downloadProperties } from "./api-instituicao-providence";
import { ADMIN } from "../../Usuario/Perfil";
import { errorMessage } from "../../../util/ConfirmAlertUtil";
import { createAndDownloadBlobFile } from "../../../util/DownloadUtil";
import { successDefault } from "../../../util/ToastUtil";
import Loading from "../../UI/Loading";
import PasswordField from "../PasswordField";
import SettingsCard from "../SettingsCard";

const SECOES = [
  {
    title: "Servidor",
    fields: [
      { name: "apiIp", label: "Api Ip" },
      { name: "impressoraBobina", label: "Impressora Bobina" },
    ],
  },
  {
    title: "Colesan Online",
    fields: [
      { name: "colesanOnlineUrl", label: "Colesan Online Url" },
      { name: "loginColesanOnline", label: "Login Colesan Online" },
      { name: "senhaColesanOnline", label: "Senha Colesan Online", secret: true },
    ],
  },
  {
    title: "Winbox (SSH)",
    fields: [
      { name: "sshIpWinbox", label: "Ssh Ip Winbox" },
      { name: "sshPortWinbox", label: "Ssh Port Winbox" },
      { name: "sshUserWinbox", label: "Ssh User Winbox" },
      { name: "sshPasswordWinbox", label: "Ssh Password Winbox", secret: true },
    ],
  },
  {
    title: "AWS",
    fields: [
      { name: "awsAccessKeyId", label: "Aws Access Key Id" },
      { name: "awsSecretAccessKey", label: "Aws Secret Access Key", secret: true },
    ],
  },
];

const ConfiguracaoInstituicaoProvidenceForm = () => {
  const usuario = useSelector((state) => state.usuario);
  const isAdmin = usuario.perfil === ADMIN;
  const [instituicaoProvidence, setInstituicaoProvidence] = useState({});
  const [isLoading, setIsLoading] = useState(isAdmin);
  const [isSaving, setIsSaving] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    if (!isAdmin) return;
    setIsLoading(true);
    findByCurrentUser()
      .then((data) => {
        if (!data) return;
        if (data.error) {
          errorMessage(data.message);
        } else {
          setInstituicaoProvidence(data);
        }
      })
      .finally(() => setIsLoading(false));
  }, [isAdmin]);

  const onChange = (event) => {
    const { name, value } = event.target;
    setInstituicaoProvidence((prevState) => ({ ...prevState, [name]: value }));
  };

  const clickSubmit = (event) => {
    event.preventDefault();
    setIsSaving(true);
    save(instituicaoProvidence)
      .then((data) => {
        if (!data) return;
        if (data.error) {
          errorMessage(data.message);
          return;
        }
        setInstituicaoProvidence(data);
        successDefault();
      })
      .finally(() => setIsSaving(false));
  };

  const handleDownload = () => {
    setIsDownloading(true);
    downloadProperties()
      .then((data) => {
        if (!data || data.error) {
          errorMessage(data?.message || "Não foi possível baixar o arquivo.");
          return;
        }
        createAndDownloadBlobFile(data, "application.properties");
      })
      .finally(() => setIsDownloading(false));
  };

  const downloadButton = (
    <Button
      size="small"
      variant="outlined"
      startIcon={<DownloadIcon />}
      disabled={isDownloading}
      onClick={handleDownload}
    >
      Download Properties
    </Button>
  );

  if (!isAdmin) {
    return (
      <SettingsCard
        title="Instituição Providence"
        description="Arquivo de configuração do Providence da instituição."
        action={downloadButton}
        contentPadding={false}
      />
    );
  }

  if (isLoading) {
    return (
      <Box py={6}>
        <Loading description="Carregando Instituição Providence..." />
      </Box>
    );
  }

  return (
    <form onSubmit={clickSubmit}>
      <Stack spacing={3}>
        <SettingsCard
          title="Instituição Providence"
          description="Configurações usadas pelo Providence da instituição."
          action={downloadButton}
        >
          <Stack spacing={3}>
            {SECOES.map((secao) => (
              <Box key={secao.title}>
                <Box component="h3" sx={{ typography: "subtitle2", color: "text.secondary", m: 0, mb: 1.5 }}>
                  {secao.title}
                </Box>
                <Grid container spacing={2}>
                  {secao.fields.map(({ name, label, secret }) => {
                    const FieldComponent = secret ? PasswordField : TextField;
                    return (
                      <Grid key={name} size={{ xs: 12, sm: 6, lg: 3 }}>
                        <FieldComponent
                          size="small"
                          fullWidth
                          label={label}
                          name={name}
                          value={instituicaoProvidence[name] ?? ""}
                          onChange={onChange}
                        />
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            ))}
          </Stack>
        </SettingsCard>
        <Box>
          <Button type="submit" variant="contained" disabled={isSaving}>
            Salvar
          </Button>
        </Box>
      </Stack>
    </form>
  );
};

export default ConfiguracaoInstituicaoProvidenceForm;
